import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Clock, MapPin, Phone } from "lucide-react";
import { PageShell, Breadcrumbs } from "@/components/layout/PageShell";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { EmptyState, ErrorState, Spinner } from "@/components/states";
import { useI18n } from "@/lib/i18n";
import { useStorefront } from "@/lib/storefront";
import { branchesQuery } from "@/lib/queries";
import { formatHours, loc } from "@/lib/format";
import { googleMapsUrl } from "@/lib/maps";

export const Route = createFileRoute("/branches_/$branchId")({ component: BranchPage });

function BranchPage() {
  const { branchId } = Route.useParams();
  const { lang, t } = useI18n();
  const storefront = useStorefront();
  const query = useQuery(branchesQuery());
  const branch = query.data?.find((item) => item.id === branchId);

  if (query.isPending) return <PageShell><Spinner /></PageShell>;
  if (query.isError) return <PageShell><ErrorState error={query.error} onRetry={() => query.refetch()} /></PageShell>;
  if (!branch) return <PageShell><EmptyState title={t("notfound.title")} description={t("notfound.desc")} /></PageShell>;

  const name = loc(branch.name, lang);
  const address = loc(branch.address, lang);
  const menuAvailable = Boolean(storefront.settings?.onlineOrderingEnabled) ||
    Boolean(storefront.settings?.dineInEnabled && branch.indoorService);
  return (
    <PageShell>
      <Breadcrumbs items={[{ label: t("nav.home"), to: "/" }, { label: t("nav.branches"), to: "/branches" }, { label: name }]} />
      <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <ResponsiveImage
          source={branch.responsiveImage ?? branch.image}
          sizes="(max-width: 767px) calc(100vw - 2rem), 50vw"
          alt={name}
          loading="eager"
          fetchPriority="high"
          className="aspect-[4/3] w-full rounded-3xl object-cover"
        />
        <div className="space-y-5">
          <h1 className="text-3xl font-black sm:text-4xl">{name}</h1>
          <p className="flex items-start gap-3 text-lg"><MapPin className="mt-1 size-5 shrink-0" aria-hidden="true" />{address}</p>
          <p className="flex items-center gap-3"><Clock className="size-5" aria-hidden="true" />{formatHours(branch.openingHours, lang)}</p>
          {branch.phones.map((phone) => (
            <a key={phone} href={`tel:${phone}`} className="flex w-fit items-center gap-3 text-primary underline-offset-4 hover:underline">
              <Phone className="size-5" aria-hidden="true" />{phone}
            </a>
          ))}
          <div className="flex flex-wrap gap-3 pt-3">
            {menuAvailable ? (
              <Link to="/branches/$branchId/menu" params={{ branchId }} className="inline-flex min-h-11 items-center rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground">
                {lang === "ar" ? "قائمة هذا الفرع" : "This branch's menu"}
              </Link>
            ) : null}
            {branch.coordinates ? (
              <a href={googleMapsUrl(branch.coordinates)} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center rounded-full border border-border px-6 text-sm font-bold">
                {t("branches.maps")}
              </a>
            ) : null}
          </div>
          {!menuAvailable ? (
            <p className="text-sm text-muted-foreground">{lang === "ar" ? "قد لا تكون قائمة هذا الفرع متاحة حاليًا." : "This branch's menu may not currently be available."}</p>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
}
